import { ColorEnum } from '@/constants/color'
import { CashTransactionType } from '@/features/graphql/generated/graphql'
import { Layout } from '@/features/layout/types'
import { CashTransactionItem } from '@/features/shiftCloseouts/types'
import { formatSignedPrice } from '@/features/shiftCloseouts/utils/formatSignedPrice'
import MinusIcon from '@/features/ui/icons/components/MinusIcon'
import PlusIcon from '@/features/ui/icons/components/PlusIcon'
import Typography from '@/features/ui/typography'
import { getCurrencySymbol } from '@/utils/priceHelper'
import dayjs from 'dayjs'
import { FC } from 'react'
import { View } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

interface TransactionRowProps {
    layout: Layout
    transaction: CashTransactionItem
}

const TransactionRow: FC<TransactionRowProps> = ({ layout, transaction }) => {
    const { styles } = useStyles(stylesheet, { layout })
    const deposit = transaction.type === CashTransactionType.Deposit
    const formattedPrice = formatSignedPrice(
        transaction.amount.amount,
        transaction.amount.currency,
        deposit ? 'positive' : 'negative'
    )
    const currency = getCurrencySymbol(transaction.amount.currency)
    const currencySuffix = ` ${currency}`
    const formattedAmount = formattedPrice.endsWith(currencySuffix)
        ? formattedPrice.slice(0, -currencySuffix.length)
        : formattedPrice
    const transactionTime = dayjs(transaction.transactedAt).format('HH:mm')

    return (
        <View style={styles.row} testID={`cash-transaction-row-${transaction.id}`}>
            <Typography.Description
                style={styles.time}
                testID={`cash-transaction-time-${transaction.id}`}
            >
                {transactionTime}
            </Typography.Description>
            <View
                style={[styles.icon, deposit ? styles.depositIcon : styles.withdrawalIcon]}
                testID={`cash-transaction-icon-${transaction.id}`}
            >
                {deposit ? (
                    <PlusIcon fill={ColorEnum.EMERALD_700} height={24} width={24} />
                ) : (
                    <MinusIcon fill={ColorEnum.BRICK_500} height={24} width={24} />
                )}
            </View>
            <View style={styles.content} testID={`cash-transaction-content-${transaction.id}`}>
                <Typography.Description font="medium" style={styles.description}>
                    {transaction.note}
                </Typography.Description>
                <View
                    accessibilityLabel={formattedPrice}
                    accessible
                    style={styles.amount}
                    testID={`cash-transaction-amount-${transaction.id}`}
                >
                    <Typography.Description
                        font="medium"
                        style={deposit ? styles.depositAmount : styles.withdrawalAmount}
                    >
                        {formattedAmount}
                    </Typography.Description>
                    <Typography.Description font="medium" style={styles.currency}>
                        {currency}
                    </Typography.Description>
                </View>
            </View>
        </View>
    )
}

const stylesheet = createStyleSheet({
    row: {
        minHeight: 48,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
        padding: 8,
        borderColor: ColorEnum.GRAY_200,
        borderRadius: 12,
        borderWidth: 1,
        backgroundColor: ColorEnum.WHITE,
    },
    content: {
        flex: 1,
        minWidth: 0,
        variants: {
            layout: {
                compact: {
                    flexDirection: 'column',
                    gap: 4,
                },
                extended: {
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                },
            },
        },
    },
    time: {
        flexShrink: 0,
        lineHeight: 16,
        color: ColorEnum.GRAY_600,
    },
    icon: {
        width: 40,
        height: 40,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
    },
    depositIcon: {
        backgroundColor: ColorEnum.EMERALD_100,
    },
    withdrawalIcon: {
        backgroundColor: ColorEnum.Brick50,
    },
    description: {
        minWidth: 0,
        lineHeight: 16,
        variants: {
            layout: {
                compact: {
                    flexShrink: 1,
                },
                extended: {
                    flex: 1,
                },
            },
        },
    },
    amount: {
        flexShrink: 0,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    depositAmount: {
        lineHeight: 16,
        color: ColorEnum.EMERALD_700,
    },
    withdrawalAmount: {
        lineHeight: 16,
        color: ColorEnum.BRICK_500,
    },
    currency: {
        lineHeight: 16,
        color: ColorEnum.GRAY_600,
    },
})

export default TransactionRow
